import './PhotosPage.css';
import React, { useEffect, useCallback } from 'react';

import './PhotoPage.css';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FbImage from '../common/components/fb_image/FbImage';
import { getPhoto, loadPhotosByGallery } from '../actions/photoActions';
import { getGallery } from '../actions/galleryActions';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { getCategory } from '../actions/categoryActions';
import { trackNavigation } from '../common/utils/analytics';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SEO from '../common/components/seo/SEO';
import { getBreadcrumbStructuredData } from '../common/utils/structuredData';

function PhotoPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const photo = useSelector(state => state.photo.photo);
    const photos = useSelector(state => state.photo.photosByGallery);
    const gallery = useSelector(state => state.gallery.gallery);
    const category = useSelector(state => state.category.category);

    const { photoId } = useParams();

    useEffect(() => {
        dispatch(getPhoto(photoId));
    }, [dispatch, photoId]);

    useEffect(() => {
        if (photo?.galleryId) {
            dispatch(getGallery(photo?.galleryId));
            dispatch(loadPhotosByGallery(photo?.galleryId));
        }
    }, [dispatch, photo]);

    useEffect(() => {
        if (gallery) dispatch(getCategory(gallery?.categoryId));
    }, [dispatch, gallery]);

    // Find current photo index and get prev/next
    const currentIndex = photos?.findIndex(p => p.id === photoId) ?? -1;
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < (photos?.length ?? 0) - 1;
    const prevPhotoId = hasPrev ? photos[currentIndex - 1]?.id : null;
    const nextPhotoId = hasNext ? photos[currentIndex + 1]?.id : null;

    // Keyboard navigation
    const handleKeyPress = useCallback((e) => {
        if (e.key === 'ArrowLeft' && prevPhotoId) {
            navigate(`/galleries/photo/${prevPhotoId}`);
        } else if (e.key === 'ArrowRight' && nextPhotoId) {
            navigate(`/galleries/photo/${nextPhotoId}`);
        } else if (e.key === 'Escape') {
            navigate(`/galleries/photos/${photo?.galleryId}`);
        }
    }, [prevPhotoId, nextPhotoId, navigate, photo?.galleryId]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [handleKeyPress]);

    const structuredData = getBreadcrumbStructuredData([
        { name: 'Home', url: '/' },
        { name: 'Galleries', url: '/galleries' },
        { name: category?.categoryName || 'Category', url: category?.id ? `/galleries/${category.id}` : undefined },
        { name: gallery?.galleryName || 'Gallery', url: gallery?.id ? `/galleries/photos/${gallery.id}` : undefined },
        { name: photo?.photoName || (currentIndex >= 0 ? `Photo ${currentIndex + 1}` : 'Photo'), url: photo?.id ? `/galleries/photo/${photo.id}` : undefined },
    ]);

    const photoImageUrl = photo?.path 
        ? (photo.path.startsWith('http') 
            ? photo.path 
            : `https://firebasestorage.googleapis.com/v0/b/photo-portfolio-ln.appspot.com/o/${encodeURIComponent(photo.path)}?alt=media`)
        : null;

    return (
        <>
            <SEO
                title={photo?.photoName || (currentIndex >= 0 ? `Photo ${currentIndex + 1}` : 'Photo')}
                description={photo?.photoName 
                    ? `${photo.photoName} - Landscape photography by ValeTer. ${gallery?.galleryName ? `Part of ${gallery.galleryName} gallery.` : ''} ${category?.categoryName ? `${category.categoryName} photography.` : ''}`
                    : 'Landscape photography by ValeTer'}
                image={photoImageUrl}
                type="article"
                keywords={photo?.photoName 
                    ? `${photo.photoName}, landscape photography, ${gallery?.galleryName || ''}, ${category?.categoryName || ''}, nature photography`
                    : 'landscape photography'}
                structuredData={structuredData}
            />
            <Paper className='photo-page'>
                <div className='photo-header'>
                <Breadcrumbs separator="›" aria-label="breadcrumb" className='enhanced-breadcrumbs'>
                    <Link 
                        component={RouterLink}
                        underline="hover" 
                        color="inherit" 
                        to="/"
                        onClick={() => trackNavigation('Breadcrumb: Home')}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                        Home
                    </Link>
                    <Link 
                        component={RouterLink}
                        underline="hover" 
                        color="inherit" 
                        to="/galleries"
                        onClick={() => trackNavigation('Breadcrumb: Galleries')}
                    >
                        Galleries
                    </Link>
                    <Link 
                        component={RouterLink}
                        underline="hover" 
                        color="inherit" 
                        to={`/galleries/${category?.id}`}
                        onClick={() => trackNavigation(`Breadcrumb: ${category?.categoryName}`)}
                    >
                        {category?.categoryName}
                    </Link>
                    <Link 
                        component={RouterLink}
                        underline="hover" 
                        color="inherit" 
                        to={`/galleries/photos/${gallery?.id}`}
                        onClick={() => trackNavigation(`Breadcrumb: ${gallery?.galleryName}`)}
                    >
                        {gallery?.galleryName}
                    </Link>
                    <Typography color="text.primary">
                        {photo?.photoName || (currentIndex >= 0 ? `Photo ${currentIndex + 1}` : 'Photo')}
                    </Typography>
                </Breadcrumbs>
            </div>

            <div className='photo-viewer'>
                <IconButton 
                    className='nav-button prev-button'
                    onClick={() => prevPhotoId && navigate(`/galleries/photo/${prevPhotoId}`)}
                    disabled={!hasPrev}
                    size="large"
                    aria-label="Previous photo"
                >
                    <ArrowBackIcon />
                </IconButton>

                <FbImage
                    path={photo?.mediumPath || photo?.path}
                    thumbnailPath={photo?.thumbnailPath}
                    alt={photo?.photoName}
                    className='main-photo'
                />

                <IconButton 
                    className='nav-button next-button'
                    onClick={() => nextPhotoId && navigate(`/galleries/photo/${nextPhotoId}`)}
                    disabled={!hasNext}
                    size="large"
                    aria-label="Next photo"
                >
                    <ArrowForwardIcon />
                </IconButton>
            </div>

            <div className='info-panel'>
                <Typography variant="body1">{photo?.photoName}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block' }}>
                    {currentIndex >= 0 && `${currentIndex + 1} of ${photos?.length ?? 0}`}
                </Typography>
            </div>
        </Paper>
        </>
    );
}

export default PhotoPage;
